import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import { Edit } from "@mui/icons-material";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import { toCurrency, toDate } from "../../utils/utils.js";

import {
	UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS,
	DATA_REVIEW_CONSTANTS,
} from "../../common/constants/dataModule/dataReviewConstants.js";
import LinearProgress from "@mui/material/LinearProgress";
import {
	SIZES,
	SORT_ORDERS,
	ALIGN,
	HTML_TAGS,
	BUTTON_COLORS,
} from "../../common/constants/commonConstants.js";
import { IconButton, Checkbox, Typography } from "@mui/material";

const CustomerByUploadTable = ({
	page,
	order,
	setPage,
	orderBy,
	rowsPerPage,
	isLoading,
	customerToDisplay,
	setRowsPerPage,
	columns,
	onEdit,
	handleRequestSort,
	filteredCustomerListLength,
	onPublishConversUpdate,
}) => {
	const [customerListByDate, setCustomerListByDate] = useState([]);

	useEffect(() => {
		setCustomerListByDate(customerToDisplay);
	}, [customerToDisplay]);

	const isAllChecked = (field) => customerListByDate.every((row) => row[field]);

	const handleSingleCheck = (id, field) => {
		const updatedList = customerListByDate.map((row) =>
			row.id === id ? { ...row, [field]: !row[field] } : row
		);

		setCustomerListByDate(updatedList);
		onPublishConversUpdate(updatedList);
	};
	const handleSelectAll = (field, checked) => {
		const updatedList = customerListByDate.map((row) => ({
			...row,
			[field]: checked,
		}));
		setCustomerListByDate(updatedList)
		onPublishConversUpdate(updatedList);
	};

	const renderTableRowCell = (type, value) => {
		switch (type) {
			case "currency":
				return toCurrency(value);
			case "date":
				return toDate(value);
			case "number":
				return Number(value);
			case "phone":
				return value.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
			default:
				return value;
		}
	};

	const visibleColumns = (columns || []).filter(
		(col) => col.id !== UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.ID
	);
	const headerCount = visibleColumns.length || 1;

	return (
		<>
			<TableContainer sx={{ mt: 2 }}>
				<Table size={SIZES.SMALL}>
					<TableHead>
						<TableRow>
							{columns.map((column) => {
								if (column.id === UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.ID)
									return null;
								if (column.id === UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.PUBLISHED)
									return (
										<TableCell>
											<>
												<Checkbox
													fontSize={SIZES.SMALL}
													color={BUTTON_COLORS.INFO}
													checked={isAllChecked("published")}
													onChange={(e) =>
														handleSelectAll("published", e.target.checked)
													}
												/>
												<Typography variant="h9"> Publish </Typography>
											</>
										</TableCell>
									);
								if (
									column.id === UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.CONVAPPROVED
								)
									return (
										<TableCell>
											{/* <> */}
											<Checkbox
												fontSize={SIZES.SMALL}
												color={BUTTON_COLORS.INFO}
												checked={isAllChecked("convapproved")}
												onChange={(e) =>
													handleSelectAll("convapproved", e.target.checked)
												}
											/>
											<Typography variant="h9"> Converse </Typography>
											{/* </> */}
										</TableCell>
									);

								return (
									<TableCell
										key={column.id}
										sortDirection={orderBy === column.id ? order : false}
										sx={{
											width: `${Math.floor(100 / headerCount)}%`,
											boxSizing: 'border-box'
										}}
									>
										{column.isSortable ? (
											<TableSortLabel
												active={orderBy === column.id}
												direction={
													orderBy === column.id ? order : SORT_ORDERS.ASCENDING
												}
												onClick={() => handleRequestSort(column.id)}
											>
												{column.label}
											</TableSortLabel>
										) : (
											column.label
										)}
									</TableCell>
								);
							})}
						</TableRow>
					</TableHead>

					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={columns.length} align={ALIGN.CENTER}>
									<LinearProgress />
								</TableCell>
							</TableRow>
						) : customerListByDate?.length > 0 ? (
							customerListByDate.map((customer) => (
								<TableRow hover tabIndex={-1} key={customer.id}>
									{columns.map((column) => {
										if (column.id === UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.ID)
											return null;

										if (
											column.id === UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.ACTIONS
										) {
											return (
												<TableCell key={column.id}>
													<IconButton onClick={() => onEdit(customer)}>
														<Edit fontSize={SIZES.SMALL} />
													</IconButton>
												</TableCell>
											);
										}

										if (
											column.id === UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.PUBLISHED
										) {
											return (
												<TableCell key={column.id}>
													<Checkbox
														fontSize={SIZES.SMALL}
														color={BUTTON_COLORS.INFO}
														checked={customer.published}
														onChange={() =>
															handleSingleCheck(customer.id, "published")
														}
													/>
												</TableCell>
											);
										}

										if (
											column.id ===
											UPLOAD_CUSTOMER_TABLE_COLUMNS_KEYS.CONVAPPROVED
										) {
											return (
												<TableCell key={column.id}>
													<Checkbox
														fontSize={SIZES.SMALL}
														color={BUTTON_COLORS.INFO}
														checked={customer.convapproved}
														onChange={() =>
															handleSingleCheck(customer.id, "convapproved")
														}
													/>
												</TableCell>
											);
										}
										return (
											<TableCell key={column.id}>
												{renderTableRowCell(column.type, customer[column.id])}
											</TableCell>
										);
									})}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={columns?.length} align={ALIGN.CENTER}>
									{DATA_REVIEW_CONSTANTS.NO_DATA}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>

				<TablePagination
					page={page}
					rowsPerPage={rowsPerPage}
					component={HTML_TAGS.DIV}
					count={filteredCustomerListLength}
					rowsPerPageOptions={[10, 25, 50]}
					onPageChange={(event, newPage) => setPage(newPage)}
					onRowsPerPageChange={(event) => {
						setPage(0);
						setRowsPerPage(parseInt(event.target.value, 10));
					}}
				/>
			</TableContainer>
		</>
	);
};

export default CustomerByUploadTable;